import { makeEsArticle, type EsArticleInput } from "./learnFactoryEs";
import type { LearnFAQ } from "@/pages/learn/learnShared";

/**
 * /es/learn/ampa-receptors-ketamine-and-tms
 */

const AMPA_TRADEMARK =
  "Ampa y Ampa One son marcas registradas de Ampa Inc. Neuro Wellness TMS Centers of America es un proveedor independiente y no pertenece a Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "¿Qué es AMPA?",
    answer:
      "AMPA tiene dos significados en el cuidado de la depresión. Los receptores AMPA son receptores de glutamato en el cerebro que ayudan a las neuronas a fortalecer sus conexiones (neuroplasticidad). Ampa One es un dispositivo de TMS no relacionado que comparte el nombre. La investigación sugiere que la ketamina y la esketamina (Spravato®) alivian la depresión en parte al aumentar la señalización de los receptores AMPA. Ambos enfoques buscan ayudar al cerebro a reconfigurarse.",
  },
  {
    question: "¿Qué tienen que ver los receptores AMPA con la depresión?",
    answer:
      "Los receptores AMPA son receptores de glutamato que ayudan a las neuronas a fortalecer sus conexiones, un proceso llamado neuroplasticidad que la depresión puede afectar. La investigación sugiere que la ketamina y la esketamina (Spravato®) alivian la depresión en parte al aumentar la señalización de los receptores AMPA, lo que ayuda al cerebro a formar nuevas conexiones. Esta es una teoría de por qué los tratamientos basados en glutamato pueden funcionar cuando los antidepresivos monoaminérgicos no lo han hecho.",
  },
  {
    question: "¿El TMS Ampa está relacionado con los receptores AMPA?",
    answer:
      "No. Ampa One es un dispositivo de TMS que comparte el nombre 'Ampa' con el receptor AMPA, pero no están relacionados. Ampa One entrega estimulación magnética a la corteza prefrontal; los receptores AMPA son proteínas en las neuronas. La coincidencia del nombre es casual. Ambos enfoques buscan ayudar al cerebro a reconfigurarse, por lo que algunas clínicas, incluida la nuestra, ofrecen ambos.",
  },
  {
    question: "¿Puedo hacer Spravato y TMS juntos?",
    answer:
      "Combinar Spravato® (esketamina) y TMS es una decisión médica basada en su historial y diagnóstico. No hay ninguna regla en contra, y algunos pacientes se benefician de un enfoque combinado, pero no es adecuado para todos. Nuestros psiquiatras deciden según su situación. Consulte [[Spravato vs ketamina|/es/spravato-vs-ketamine]] y [[TMS vs Spravato|/es/learn/tms-vs-spravato]].",
  },
];

export const esAmpaReceptorsKetamineAndTms = makeEsArticle({
  slug: "ampa-receptors-ketamine-and-tms",
  title: "Receptores AMPA, ketamina y TMS Ampa One: ¿cuál es la conexión?",
  description:
    "Los receptores AMPA son receptores de glutamato vinculados a la neuroplasticidad; la ketamina y Spravato® pueden funcionar en parte a través de ellos. Ampa One TMS comparte el nombre pero no está relacionado. Ofrecemos ambos enfoques.",
  h1: '"AMPA" en el cuidado de la depresión: el receptor cerebral frente al dispositivo de TMS',
  breadcrumbLabel: "Receptores AMPA, ketamina y TMS Ampa One",
  shortAnswer:
    "Los receptores AMPA son receptores de glutamato en el cerebro que ayudan a las neuronas a fortalecer sus conexiones (neuroplasticidad). La investigación sugiere que la ketamina y la esketamina (Spravato®) alivian la depresión en parte al aumentar la señalización de los receptores AMPA. Ampa One es un dispositivo de TMS no relacionado que comparte el nombre. Ambos enfoques buscan ayudar al cerebro a reconfigurarse, por lo que algunas clínicas, incluida la nuestra, ofrecen ambos. Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica.",
  keyTakeaways: [
    "Los receptores AMPA son receptores de glutamato que ayudan a las neuronas a fortalecer conexiones (neuroplasticidad).",
    "La ketamina y la esketamina (Spravato®) pueden aliviar la depresión en parte al aumentar la señalización de los receptores AMPA.",
    "Ampa One es un dispositivo de TMS que comparte el nombre 'Ampa' pero no está relacionado con el receptor.",
    "Ambos enfoques buscan ayudar al cerebro a reconfigurarse, por lo que algunas clínicas ofrecen ambos.",
    "Esta página debe ser revisada por un médico antes de publicarse.",
  ],
  sections: [
    {
      heading: "Respuesta rápida",
      paragraphs: [
        "Los receptores AMPA son receptores de glutamato en el cerebro que ayudan a las neuronas a fortalecer sus conexiones (neuroplasticidad). La investigación sugiere que la ketamina y la esketamina (Spravato®) alivian la depresión en parte al aumentar la señalización de los receptores AMPA. Ampa One es un dispositivo de TMS no relacionado que comparte el nombre. Ambos enfoques buscan ayudar al cerebro a reconfigurarse, por lo que algunas clínicas, incluida la nuestra, ofrecen ambos.",
      ],
    },
    {
      heading: "Qué hacen los receptores AMPA",
      paragraphs: [
        "Los receptores AMPA son un tipo de receptor de glutamato, el receptor excitatorio más común del cerebro. Cuando el glutamato se une a un receptor AMPA, la neurona tiene más probabilidad de disparar, y la activación repetida ayuda a fortalecer la conexión entre dos neuronas. Ese fortalecimiento se llama neuroplasticidad y es la base del aprendizaje y la adaptación. La depresión se asocia con una menor neuroplasticidad en regiones clave del cerebro, por lo que los tratamientos que la restauran son un foco importante de la investigación actual.",
      ],
    },
    {
      heading: "Cómo la ketamina y Spravato® involucran a los receptores AMPA",
      paragraphs: [
        "Se cree que la ketamina y la esketamina (Spravato®) alivian la depresión en parte al aumentar la señalización de los receptores AMPA. La ketamina bloquea el receptor NMDA, lo que desencadena una cascada que aumenta la actividad de los receptores AMPA y, a su vez, favorece la neuroplasticidad. Esta es una de las teorías principales de por qué los tratamientos basados en glutamato pueden funcionar rápidamente en pacientes que no han respondido a los antidepresivos monoaminérgicos. Consulte [[Spravato vs ketamina|/es/spravato-vs-ketamine]].",
      ],
    },
    {
      heading: "Qué es el TMS Ampa One",
      paragraphs: [
        "Ampa One es un dispositivo de estimulación magnética transcraneal (TMS) autorizado por la FDA para el trastorno depresivo mayor en adultos que no han mejorado lo suficiente con antidepresivos. Usa orientación guiada por cámara con un gorro preimpreso y entrega pulsos magnéticos a la corteza prefrontal dorsolateral izquierda para estimular esa región. Comparte el nombre 'Ampa' con el receptor AMPA pero no está relacionado. Consulte [[TMS Ampa One en Florida|/es/ampa]].",
      ],
    },
    {
      heading: "¿Se pueden combinar la ketamina/Spravato® y el TMS?",
      paragraphs: [
        "Combinar ketamina o Spravato® con TMS es una decisión médica basada en su historial y diagnóstico. No hay ninguna regla en contra, y algunos pacientes se benefician de un enfoque combinado que aborda la depresión desde más de un ángulo, pero no es adecuado para todos. Nuestros psiquiatras deciden según su situación. Consulte [[TMS vs Spravato|/es/learn/tms-vs-spravato]] y [[Spravato vs ketamina|/es/spravato-vs-ketamine]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Duman RS, Aghajanian GK. Synaptic dysfunction in depression: potential therapeutic targets. Science. 2012;338(6103):68-72.",
      url: "https://www.science.org/doi/10.1126/science.1222939",
    },
    {
      citation:
        "Ampa Inc. Ampa One — sistema de TMS autorizado por la FDA para el trastorno depresivo mayor.",
      url: "https://www.ampahealth.com/",
    },
  ],
  relatedLinks: [
    {
      title: "TMS Ampa One™ en Florida",
      href: "/es/ampa",
      description: "Tratamiento de depresión acelerado y guiado por cámara",
    },
    {
      title: "Spravato® (Esketamina)",
      href: "/es/spravato",
      description:
        "Espray nasal autorizado por la FDA para la depresión resistente al tratamiento",
    },
    {
      title: "Spravato vs Ketamina",
      href: "/es/spravato-vs-ketamine",
      description: "Cómo se comparan ambos",
    },
    {
      title: "TMS vs Spravato",
      href: "/es/learn/tms-vs-spravato",
      description: "Comparación del TMS y la esketamina para la depresión",
    },
    {
      title: "Solicitar una consulta",
      href: "/es/contactus",
      description: "Reserve una consulta gratuita con nuestro equipo clínico",
    },
  ],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "3 de noviembre de 2026",
    lastReviewed: "3 de noviembre de 2026",
    lastUpdated: "3 de noviembre de 2026",
  },
  toc: [
    { label: "Respuesta rápida", href: "#respuesta-rapida" },
    { label: "Receptores AMPA", href: "#que-hacen-los-receptores-ampa" },
    {
      label: "Ketamina y Spravato®",
      href: "#como-la-ketamina-y-spravato-involucran-a-los-receptores-ampa",
    },
    { label: "TMS Ampa One", href: "#que-es-el-tms-ampa-one" },
    {
      label: "Combinarlos",
      href: "#se-pueden-combinar-la-ketaminaspravato-y-el-tms",
    },
    { label: "Preguntas frecuentes", href: "#faq" },
    { label: "Investigación citada", href: "#cited-research" },
  ],
  safetyBlock:
    "Los resultados individuales varían. No se garantiza ningún resultado. La cobertura varía según el plan. Ampa One está disponible con receta tras una evaluación clínica. Si está en crisis, llame o envíe un mensaje de texto al 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "Hable sobre sus opciones con nuestro equipo, sin costo",
    primaryText: "Solicitar una consulta gratuita",
    primaryHref: "/es/ampa",
    secondaryText: "Llamar 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
} as EsArticleInput);
